/**
 * How each entity's records render in the chat — the same columns the page
 * shows, the same cell formatting, and where a row opens.
 *
 * The runs and spans entities reuse the page's own tables (AgentRunsTable,
 * SpansTable) and need no column definitions here; everything else renders
 * through GenericRecordsTable with the definitions below, which mirror the
 * headers of the product's list pages. A column's `format` is a formatter
 * name from chatCellFormat; `link` says what a row opens.
 */

// path is relative to the team; the client prefixes "/<team>".
export const ENTITY_PAGES = {
  runs: { path: '/agent-analytics/runs', label: 'Open in Runs' },
  spans: { path: '/agent-analytics/traces', label: 'Open in Traces' },
  conversations: { path: '/agent-analytics/conversations', label: 'Open in Conversations' },
  users: { path: '/activity/users', label: 'Open in Users' },
  agents: { path: '/overview', label: 'Open in Overview' },
  capabilities: { path: '/agent-analytics/capabilities', label: 'Open in Capabilities' },
  evals: { path: '/agent-analytics/evals', label: 'Open in Evals' },
  alerts: { path: '/alerts', label: 'Open in Alerts' },
  // The page lands on Failures; `tab: 'issues'` opens its Issues tab.
  issues: { path: '/agent-analytics/issues', params: { tab: 'issues' }, label: 'Open in Issues' },
  prompts: { path: '/prompts', label: 'Open in Prompts' },
  datasets: { path: '/datasets', label: 'Open in Datasets' },
  playgrounds: { path: '/playground', label: 'Open in Playground' },
  // Comments live on the trace they were left on, so that is where they open.
  annotations: { path: '/agent-analytics/traces', label: 'Open in Traces' },
};

/** Where one row opens: a function of the row, relative to the team. */
export const ROW_LINKS = {
  runs: (r) => (r.run_id ? `/agent-analytics/traces?run=${encodeURIComponent(r.run_id)}` : null),
  spans: (r) => (r.span_id ? `/agent-analytics/traces?span=${encodeURIComponent(r.span_id)}` : null),
  conversations: (r) => (r.conversation_id ? `/agent-analytics/conversations?conversation=${encodeURIComponent(r.conversation_id)}` : null),
  users: (r) => (r.distinct_id ? `/activity/users/${encodeURIComponent(r.distinct_id)}` : null),
  agents: (r) => (r.agent_name ? `/agent-analytics/traces?agent_name=${encodeURIComponent(r.agent_name)}` : null),
  capabilities: (r) => (r.id ? `/agent-analytics/capabilities?capability=${encodeURIComponent(r.id)}` : null),
  evals: (r) => (r.id ? `/agent-analytics/evals?eval=${encodeURIComponent(r.id)}` : null),
  alerts: (r) => (r.id ? `/alerts/${encodeURIComponent(r.id)}` : null),
  issues: (r) => (r.id ? `/agent-analytics/issues?tab=issues&issue=${encodeURIComponent(r.id)}` : null),
  prompts: (r) => (r.id ? `/prompts/${encodeURIComponent(r.id)}` : null),
  datasets: (r) => (r.id ? `/datasets/${encodeURIComponent(r.id)}` : null),
  playgrounds: (r) => (r.id ? `/playground/${encodeURIComponent(r.id)}` : null),
  annotations: (r) => (r.run_id ? `/agent-analytics/traces?run=${encodeURIComponent(r.run_id)}` : null),
};

// format: 'text' | 'number' | 'currency' | 'duration' | 'time' | 'bool' | 'list' | 'percent' | 'id'
const c = (key, label, format = 'text') => ({ key, label, format });

export const RECORD_COLUMNS = {
  conversations: [c('conversation_id', 'Conversation', 'id'), c('distinct_id', 'User', 'id'), c('agents', 'Agents', 'list'), c('run_count', 'Runs', 'number'), c('error_count', 'Errors', 'number'), c('total_cost', 'Cost', 'currency'), c('span_ms', 'Elapsed', 'duration'), c('last_run_at', 'Last activity', 'time')],
  users: [c('distinct_id', 'User', 'id'), c('is_identified', 'Identified', 'bool'), c('sessions', 'Sessions', 'number'), c('device_type', 'Device'), c('first_location_country', 'Country'), c('last_visit', 'Last seen', 'time')],
  agents: [c('agent_name', 'Agent'), c('run_count', 'Runs', 'number'), c('error_count', 'Errors', 'number'), c('total_cost', 'Cost', 'currency'), c('last_seen_at', 'Last run', 'time')],
  capabilities: [c('name', 'Capability'), c('agent_name', 'Agent'), c('definition', 'Description'), c('run_count', 'Runs', 'number'), c('satisfaction_score', 'Satisfaction', 'percent')],
  alerts: [c('name', 'Name'), c('condition', 'Watches'), c('state', 'State'), c('last_value', 'Last value', 'number'), c('triggered_30d', 'Fired · 30d', 'number'), c('last_triggered_at', 'Last fired', 'time')],
  issues: [c('title', 'Issue'), c('agent_name', 'Agent'), c('status', 'Status'), c('verification', 'Verification'), c('occurrences', 'Occurrences', 'number'), c('updated_at', 'Updated', 'time')],
  prompts: [c('name', 'Name'), c('description', 'Description'), c('current_version', 'Version', 'number'), c('tags', 'Labels', 'list'), c('created_by', 'Creator'), c('updated_at', 'Updated', 'time')],
  datasets: [c('name', 'Name'), c('description', 'Description'), c('item_count', 'Items', 'number'), c('updated_at', 'Updated', 'time')],
  playgrounds: [c('name', 'Name'), c('current_version', 'Version', 'number'), c('version_count', 'Versions', 'number'), c('updated_at', 'Updated', 'time')],
  // The comment itself is the record, so it leads and is not truncated to an id.
  annotations: [c('body', 'Annotation'), c('context_type', 'On'), c('run_id', 'Run', 'id'), c('created_at', 'When', 'time')],
};

/** The server's column list wins when it names keys; otherwise the registry. */
export function columnsFor(entity, serverColumns) {
  const registry = RECORD_COLUMNS[entity] || [];
  if (!Array.isArray(serverColumns) || !serverColumns.length) return registry;
  const byKey = new Map(registry.map((col) => [col.key, col]));
  return serverColumns.map((sc) => byKey.get(sc.key) || c(sc.key, sc.label || sc.key));
}

/**
 * The name of the page a link actually goes to.
 *
 * The label used to come from the entity while the href came from the
 * lookup's `open.path`, so the two could disagree — a runs table whose link
 * pointed at the Runs page still said "Open in Traces". Reading the label off
 * the resolved path makes that impossible.
 */
export function openLabel(entity, open) {
  const path = open?.path || ENTITY_PAGES[entity]?.path;
  const match = Object.values(ENTITY_PAGES).find((p) => p.path === path);
  return match?.label || ENTITY_PAGES[entity]?.label || 'Open page';
}

export function openHref(teamSlug, entity, open) {
  // An explicit null means the lookup decided there is nowhere honest to send
  // you — a list of comments spanning several runs has no single page that
  // shows exactly those rows. Falling back to the entity's page would open an
  // unfiltered list, which looks like the link did something and did not.
  if (open === null) return null;
  const page = ENTITY_PAGES[entity];
  const path = open?.path || page?.path;
  if (!path) return null;
  const params = new URLSearchParams();
  const base = path === page?.path ? page?.params || {} : {};
  for (const [k, v] of Object.entries({ ...base, ...(open?.params || {}) })) if (v != null && v !== '') params.set(k, String(v));
  const qs = params.toString();
  return `/${encodeURIComponent(teamSlug || '')}${path}${qs ? `?${qs}` : ''}`;
}
